import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import type { PhoneNumberPerformance } from './api';

// =====================================================================
// The four health components, per number — what the dialer actually scores on.
//
// Deliberately a SEPARATE view from Performance, not five more columns on it. They
// answer different questions and are easy to confuse:
//
//   Performance = what HAPPENED   (answered 4 of 10 → 40%, raw, unadjusted)
//   Health      = what we BELIEVE (connection 0.48 — mostly the market's average,
//                                  because 10 calls isn't evidence yet)
//
// Both are true at once. Showing them side by side in one grid invites reading a
// shrunk score as a raw rate, which is exactly the confusion the sample floor exists
// to prevent.
// =====================================================================

type SortKey = 'number' | 'health_score' | 'maturity' | 'connection' | 'engagement' | 'sentiment';

// 0..1 → a bar plus the number. A bar alone can't be read precisely and a number alone
// can't be scanned; the pair is legible either way.
//
// `computed` is load-bearing, NOT decoration. `phone_numbers.health_score` defaults to
// 1.0 in the database and that default means "unknown", not "perfect" — so a number the
// hourly job has never scored arrives here as a confident 1.0. Rendering that as a full
// bar is exactly the trap this whole feature exists to kill (the old dead health_score
// read a reassuring 1.0 for every number while being computed by nothing). If it was
// never computed, say so — never draw it.
function Component({
  value,
  sample,
  computed,
}: {
  value: number | null;
  sample: number;
  computed: boolean;
}) {
  if (value === null || !computed) {
    return <span className="text-muted-foreground text-[0.65rem] italic">not computed</span>;
  }

  const pct = Math.round(value * 100);
  // With no calls the score is entirely the peer prior, so it is not this number's
  // verdict — muted, never coloured, or it reads as a measurement it isn't.
  const proven = sample > 0;

  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="bg-muted h-1 w-10 overflow-hidden rounded-full">
        <div
          className={cn('h-full rounded-full', proven ? 'bg-foreground/60' : 'bg-foreground/20')}
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
      <span className={cn('w-7 text-right tabular-nums', !proven && 'text-muted-foreground')}>
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function Th({
  children,
  onClick,
  active,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  title?: string;
}) {
  return (
    <th className="px-2 py-1 text-right font-medium" title={title}>
      <button
        onClick={onClick}
        className={cn('hover:text-foreground', active && 'text-foreground font-semibold')}
      >
        {children}
      </button>
    </th>
  );
}

export function NumbersHealth({
  rows,
  minSample,
}: {
  rows: PhoneNumberPerformance[];
  minSample: number;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'health_score',
    dir: 'asc',
  });

  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      if (sort.key === 'number') return a.number.localeCompare(b.number) * dir;
      const av = a[sort.key];
      const bv = b[sort.key];
      // Never-computed sorts last either way — it isn't a low score, it's no score.
      if (av === null && bv === null) return a.number.localeCompare(b.number);
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * dir;
    });
  }, [rows, sort]);

  const toggle = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  const computed = rows.filter((r) => r.health_computed_at !== null).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="bg-muted/40 rounded-md border p-2">
        <div className="text-[0.65rem] leading-relaxed">
          What the dialer <i>believes</i> about each number — the four parts of Health, each 0 to 1,
          higher is better.
        </div>
        <ul className="text-muted-foreground mt-1 space-y-0.5 text-[0.65rem]">
          <li>
            • <b>Maturity</b> how established it is · <b>Connection</b> do people answer ·{' '}
            <b>Engagement</b> do they stay on · <b>Sentiment</b> do they complain
          </li>
          <li>
            • These are <b>not</b> the raw rates on the Performance tab. A number with few calls
            reads close to its market&rsquo;s average rather than its own thin record — so
            &ldquo;answered 0 of 4&rdquo; and &ldquo;connection 0.48&rdquo; are both true.
          </li>
          <li>
            • <b>Greyed rows have no calls yet</b> ({minSample} needed to be judged on their own
            record). Their scores are their neighbours&rsquo;, not their own.
          </li>
        </ul>
      </div>

      {computed === 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[0.65rem] text-amber-900">
          Nothing has been scored yet. Health is recomputed hourly, and only from calls placed since
          per-number tracking was switched on — so this fills in as calls accumulate.
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-md border">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted/50 text-muted-foreground sticky top-0">
            <tr>
              <th className="px-2 py-1 font-medium">
                <button
                  onClick={() => toggle('number')}
                  className={cn('hover:text-foreground', sort.key === 'number' && 'font-semibold')}
                >
                  Number
                </button>
              </th>
              <Th
                onClick={() => toggle('health_score')}
                active={sort.key === 'health_score'}
                title="The weighted composite of the four components — what the selector uses."
              >
                Health
              </Th>
              <Th
                onClick={() => toggle('maturity')}
                active={sort.key === 'maturity'}
                title="Age since purchase, ramping to 1 over 30 days."
              >
                Maturity
              </Th>
              <Th
                onClick={() => toggle('connection')}
                active={sort.key === 'connection'}
                title="Answer rate, shrunk toward its market's average."
              >
                Connection
              </Th>
              <Th
                onClick={() => toggle('engagement')}
                active={sort.key === 'engagement'}
                title="1 minus the short-call rate — do people stay on the call."
              >
                Engagement
              </Th>
              <Th
                onClick={() => toggle('sentiment')}
                active={sort.key === 'sentiment'}
                title="1 minus the do-not-call rate. A proxy: DNCs are credited to whichever number last called that contact."
              >
                Sentiment
              </Th>
              <th className="px-2 py-1 text-right font-medium" title="Calls the score is based on.">
                Based on
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((n) => (
              <tr key={n.id} className={cn('border-t', n.health_sample === 0 && 'opacity-60')}>
                <td className="px-2 py-1 font-mono whitespace-nowrap">{n.number}</td>
                <td className="px-2 py-1">
                  <Component
                    value={n.health_score}
                    sample={n.health_sample}
                    computed={n.health_computed_at !== null}
                  />
                </td>
                <td className="px-2 py-1">
                  <Component
                    value={n.maturity}
                    sample={n.health_sample}
                    computed={n.health_computed_at !== null}
                  />
                </td>
                <td className="px-2 py-1">
                  <Component
                    value={n.connection}
                    sample={n.health_sample}
                    computed={n.health_computed_at !== null}
                  />
                </td>
                <td className="px-2 py-1">
                  <Component
                    value={n.engagement}
                    sample={n.health_sample}
                    computed={n.health_computed_at !== null}
                  />
                </td>
                <td className="px-2 py-1">
                  <Component
                    value={n.sentiment}
                    sample={n.health_sample}
                    computed={n.health_computed_at !== null}
                  />
                </td>
                <td className="text-muted-foreground px-2 py-1 text-right tabular-nums">
                  {n.health_sample === 0 ? (
                    <span className="text-[0.65rem] italic">no calls</span>
                  ) : (
                    `${n.health_sample} calls`
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {rows.length === 0 && (
          <div className="text-muted-foreground p-4 text-center text-xs">No caller IDs yet.</div>
        )}
      </div>
    </div>
  );
}
